import styled from 'styled-components';
import { colors, spacing } from 'ui/theme';
import { GhostButton } from 'ui/button';
import { JsonView } from 'ui/treeView';
import { Expand } from 'ui/icons/action/Expand';
import { InAppContentDefinition, PushContentDefinition } from 'modules/graphql/types';
import { useExpansionContext } from 'utils/contexts/expansion';
import { isValidJson } from 'utils/stringHelpers';

import { SettingsReadOnlyText } from './EditorContentContainer.styled';
import { RAW_DATA_JSON_ROW, SETTINGS_DISPLAY_CONTENT } from './constants';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { usePreviewWithDataContext } from '../context/PreviewWithDataContext';

const JsonViewWrapper = styled.div`
  border-radius: ${spacing.half};
  padding: ${spacing.base};
  border: ${spacing.single} solid ${colors.grey15};
  overflow: hidden;
  background-color: ${colors.grey10};
  position: relative;
`;

const StyledReadOnlyText = styled(SettingsReadOnlyText)`
  overflow-wrap: anywhere;
`;

const ExpandIconWrapper = styled(GhostButton)`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing.values.half};
  right: ${({ theme }) => theme.spacing.values.half};
`;

type ContentDefinition = InAppContentDefinition | PushContentDefinition;

export const RawPreviewField = ({ populatedPayload = '' }: { populatedPayload?: string }) => {
  const { toggle } = useExpansionContext();
  const { liveTemplate } = useEditableFieldsContext();
  const { isDataFeedDataEmpty, isUserDataEmpty } = usePreviewWithDataContext();
  const data = (liveTemplate?.mediumData || {}) as ContentDefinition;

  const usePopulatedField =
    isValidJson(populatedPayload) && (!isDataFeedDataEmpty || !isUserDataEmpty);
  const payload = usePopulatedField ? populatedPayload : data.payload;

  // this preview may be visible while constructing the json (the payload will be intermittently
  // invalid/incomplete), so it is important not to try and parse the invalid json or an error will
  // occur
  const parsedJson = isValidJson(payload ?? '{}') ? JSON.parse(payload ?? '{}') : {};
  const isJsonView = !!Object.keys(parsedJson).length;
  const hasNestedValues = !!Object.values(parsedJson).find((value) => typeof value === 'object');

  return isJsonView ? (
    <JsonViewWrapper>
      <JsonView src={parsedJson} showToggle={false} />
      {hasNestedValues && (
        <ExpandIconWrapper onClick={toggle}>
          <Expand color={colors.textSecondary} />
        </ExpandIconWrapper>
      )}
    </JsonViewWrapper>
  ) : (
    <StyledReadOnlyText isUnset data-test={RAW_DATA_JSON_ROW}>
      {SETTINGS_DISPLAY_CONTENT.unsetText}
    </StyledReadOnlyText>
  );
};
